import { Suspense } from 'react';
import { lazyImport } from '@/lib/lazyImport';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ArcadiaAuthProvider } from '@/providers/arcadia-auth';
import { ViteAppShell } from '@/components/layout/ViteAppShell';
import { OnlineStatusTracker } from '@/components/OnlineStatusTracker';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageViewTracker } from '@/components/PageViewTracker';
import { Toaster } from 'sonner';
import { AppLoadingSkeleton, AppRouteLoadingSkeleton } from '@/components/layout/AppLoadingSkeleton';

const LandingPage = lazyImport(() => import('@/pages/LandingPage'));
const AuthPage = lazyImport(() => import('@/pages/AuthPage'));
const StorePage = lazyImport(() => import('@/pages/StorePage'));
const StoreGameDetailPage = lazyImport(() => import('@/pages/StoreGameDetailPage'));
const StoreSubmitPage = lazyImport(() => import('@/pages/StoreSubmitPage'));
const StoreGameReviewsPage = lazyImport(() => import('@/pages/StoreGameReviewsPage'));
const StoreGameEditPage = lazyImport(() => import('@/pages/StoreGameEditPage'));
const LibraryPage = lazyImport(() => import('@/pages/LibraryPage'));
const LibraryGameDetailPage = lazyImport(() => import('@/pages/LibraryGameDetailPage'));
const ProfilePage = lazyImport(() => import('@/pages/ProfilePage'));
const SettingsPage = lazyImport(() => import('@/pages/SettingsPage'));
const CommunityPage = lazyImport(() => import('@/pages/CommunityPage'));
const CommunityNewThreadPage = lazyImport(() => import('@/pages/CommunityNewThreadPage'));
const CommunityThreadPage = lazyImport(() => import('@/pages/CommunityThreadPage'));
const DeveloperPage = lazyImport(() => import('@/pages/DeveloperPage'));
const DeveloperDocPage = lazyImport(() => import('@/pages/DeveloperDocPage'));
const GamePlayPage = lazyImport(() => import('@/pages/GamePlayPage'));
const AdminGamesPage = lazyImport(() => import('@/pages/AdminGamesPage'));
const DeveloperPortalLayout = lazyImport(() => import('@/pages/developer-portal/DeveloperPortalLayout'));
const PortalGamesPage = lazyImport(() => import('@/pages/developer-portal/PortalGamesPage'));
const PortalTeamPage = lazyImport(() => import('@/pages/developer-portal/PortalTeamPage'));
const PortalGameLayout = lazyImport(() => import('@/pages/developer-portal/PortalGameLayout'));
const PortalListingTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalListingTab'));
const PortalNewsTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalNewsTab'));
const PortalAnnouncementsTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalAnnouncementsTab'));
const PortalContentTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalContentTab'));
const PortalAchievementsTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalAchievementsTab'));
const PortalBuildTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalBuildTab'));
const PortalModerationTab = lazyImport(() => import('@/pages/developer-portal/tabs/PortalModerationTab'));
const NotFoundPage = lazyImport(() => import('@/pages/NotFoundPage'));
export default function App() {
  return (
    <ErrorBoundary>
        <ArcadiaAuthProvider>
          <OnlineStatusTracker />
          <Suspense fallback={null}>
            <PageViewTracker />
          </Suspense>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<div className="min-h-screen bg-arc-bg" />}>
                  <AuthPage />
                </Suspense>
              }
            />
            <Route path="/auth" element={<Navigate to="/login" replace />} />
            <Route
              element={
                <Suspense fallback={<AppLoadingSkeleton />}>
                  <ViteAppShell />
                </Suspense>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<div className="min-h-screen bg-arc-bg" />}>
                    <LandingPage />
                  </Suspense>
                }
              />
              <Route
                path="store/submit"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <StoreSubmitPage />
                  </Suspense>
                }
              />
              <Route
                path="store/:slug/reviews"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <StoreGameReviewsPage />
                  </Suspense>
                }
              />
              <Route
                path="store/:slug/edit"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <StoreGameEditPage />
                  </Suspense>
                }
              />
              <Route
                path="store/:slug"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <StoreGameDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="store"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <StorePage />
                  </Suspense>
                }
              />
              <Route
                path="library"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <LibraryPage />
                  </Suspense>
                }
              />
              <Route
                path="library/:slug"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <LibraryGameDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="profile"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <ProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="community"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <CommunityPage />
                  </Suspense>
                }
              />
              <Route
                path="community/new"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <CommunityNewThreadPage />
                  </Suspense>
                }
              />
              <Route
                path="community/:threadId"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <CommunityThreadPage />
                  </Suspense>
                }
              />
              <Route
                path="developer"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <DeveloperPage />
                  </Suspense>
                }
              />
              <Route
                path="developer/:slug"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <DeveloperDocPage />
                  </Suspense>
                }
              />
              <Route
                path="games/:gameId/play"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <GamePlayPage />
                  </Suspense>
                }
              />
              <Route
                path="admin/games"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <AdminGamesPage />
                  </Suspense>
                }
              />
              <Route
                path="developer/portal"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <DeveloperPortalLayout />
                  </Suspense>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={<AppRouteLoadingSkeleton />}>
                      <PortalGamesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="team"
                  element={
                    <Suspense fallback={<AppRouteLoadingSkeleton />}>
                      <PortalTeamPage />
                    </Suspense>
                  }
                />
                <Route
                  path="games/:gameId"
                  element={
                    <Suspense fallback={<AppRouteLoadingSkeleton />}>
                      <PortalGameLayout />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="listing" replace />} />
                  <Route
                    path="listing"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalListingTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="news"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalNewsTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="announcements"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalAnnouncementsTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="content"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalContentTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="achievements"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalAchievementsTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="build"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalBuildTab />
                      </Suspense>
                    }
                  />
                  <Route
                    path="moderation"
                    element={
                      <Suspense fallback={<AppRouteLoadingSkeleton />}>
                        <PortalModerationTab />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<AppRouteLoadingSkeleton />}>
                    <NotFoundPage />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
          <Toaster position="top-right" theme="dark" />
        </ArcadiaAuthProvider>
    </ErrorBoundary>
  );
}
